import styles from "./mortgage-refinance.module.css"
import React from "react"
import {Wizard} from "activities/components/wizard"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as calcInputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as refinance from "activities/components/refinance"
import Line from "story/components/graphs/line" // TODO: Move to shared
import * as loan from "shared/loan-payoff"
import * as graphUtil from "shared/graphs/util"
import * as finance from "shared/finance"
import * as input from "shared/components/inputs"
import AccessibilityTable from "activities/components/shared/accessibility-table"

const graphDataMortgage = (balance, currentMonthly, currentRate, currentTerm, refinanceTerm, refinanceRate, cashOut, closingCosts) => {
  const refinanceBalance = balance + cashOut + closingCosts
  const months = currentTerm > refinanceTerm ? currentTerm : refinanceTerm
  const refinanceMonthly = loan.calculatePayment(refinanceBalance, refinanceRate, refinanceTerm)
  let interestCurrent = 0
  let interestRefinance = 0
  let interestSaved = 0
  let breakEven = null
  const data = Array.from(Array(Math.ceil(months + 12))).reduce((memo, x, idx) => {
    if (idx === 0) memo.push([idx, balance, refinanceBalance])
    else {
      const last = memo[memo.length - 1]
      const currentLast = last[1]
      const refinanceLast = last[2]
      const currentPrincipal = loan.getPrincipalPayment(currentLast, currentMonthly, currentRate)
      const refinancePrincipal = loan.getPrincipalPayment(refinanceLast, refinanceMonthly, refinanceRate)
      interestCurrent += currentMonthly - currentPrincipal
      interestRefinance += refinanceMonthly - refinancePrincipal
      interestSaved = interestCurrent - interestRefinance
      breakEven = interestSaved >= closingCosts && !breakEven ? idx : breakEven
      // point where interestSaved equals closingCosts
      memo.push([idx, loan.minusPayment(currentLast, currentPrincipal), loan.minusPayment(refinanceLast, refinancePrincipal)])
    }
    return memo
  }, [])
  return {data, interestCurrent, interestRefinance, refinanceMonthly, breakEven}
}

// page 1 of the wizard
const currentLoanDetails = ({updateState, state}) => {
  const {balance, currentPayment, currentRate} = state
  return (
    <div className={styles["first-page"]}>
      <div>
        <input.Label htmlFor="loan-balance">Current Loan Balance</input.Label>
        <input.InputNoFocus id="loan-balance" value={balance} onChange={updateState("balance", util.fromString)} placeholder={"$250,000"} />
      </div>
      <div className={styles["input-range-group"]}>
        <input.Label htmlFor="current-payment">Current Payment</input.Label>
        <input.InputNoFocus id="current-payment" value={currentPayment} onChange={updateState("currentPayment", util.fromString)} placeholder={"$1,200"} />
      </div>
      <div className={styles["input-range-group"]}>
        <calcInputs.RangeInput
          initialValue={currentRate}
          onChange={updateState("currentRate", parseFloat)}
          title="Interest Rate"
          valueType="percent"
          min={0}
          max={20}
          step={0.05}
          customClass="mortgage-refinance"
        />
      </div>
    </div>
  )
}

const PaymentsCallouts = ({currentPayment, newPayment, totalCostIncrease, isDisabled}) => (
  <callouts.Callout>
    <callouts.Detail label="Current Monthly Payment" result={isDisabled ? "UNKNOWN" : currentPayment} tooltipText="Your current monthly mortgage payment." />
    <callouts.Detail label="New Monthly Payment" result={isDisabled ? "UNKNOWN" : newPayment} tooltipText="Your new monthly mortgage payment after refinancing." />
    <callouts.Detail label="Total Cost Increase" result={isDisabled ? "UNKNOWN" : totalCostIncrease} tooltipText="The total increase in interest paid over your loan term." />
  </callouts.Callout>
)

const TermLengthCallouts = ({currentTotalCost, newTotalCost, totalSavings, breakEven, isDisabled}) => (
  <callouts.Callout>
    <callouts.Detail
      label="Current Total Cost"
      result={isDisabled ? "UNKNOWN" : currentTotalCost}
      tooltipText="Total costs paid for principal and interest over your current loan term."
    />
    <callouts.Detail
      label="New Total Cost"
      result={isDisabled ? "UNKNOWN" : newTotalCost}
      tooltipText="Total cost paid for principal and interest after refinancing to a shorter loan term."
    />
    <callouts.Detail
      label="Total Savings"
      result={isDisabled ? "UNKNOWN" : totalSavings}
      tooltipText="Total amount saved by refinancing from your current to shorter mortgage term."
    />
    <callouts.Detail
      label="Break Even"
      result={isDisabled ? "UNKNOWN" : breakEven ? `${breakEven} Months` : "None"}
      tooltipText="The point at which monthly interest savings will exceed refinancing costs."
    />
  </callouts.Callout>
)

const dropdownId = "mortgage-refinance-focus"

const Inputs = ({props, state, updateState, refTerm}) => {
  const {focus, refinanceRate, cashOut, closingCosts, resetKey} = state
  const {theme, id} = props
  return (
    <div className={styles["inputs"]}>
      <calcInputs.Label inputId={`${dropdownId}-select`}>Focus on Reducing</calcInputs.Label>
      <calcInputs.Dropdown id={dropdownId} options={["Term Length", "Monthly Payments"]} onChange={updateState("focus")} value={focus} className={styles["dropdown"]} />
      <div className={styles["refinanced-mortgage"]}>
        <calcInputs.Label className="overline">Refinanced Mortgage</calcInputs.Label>
        <div className={styles["inputs-grid"]}>
          <calcInputs.RangeInput
            id="refinance-term-input"
            title="New Term"
            onChange={updateState("refinanceTerm", (v) => v * 12)}
            initialValue={refTerm}
            min={0}
            max={30}
            step={1}
            units="years"
            valueType="years-full"
            resetKey={resetKey}
            color={theme["color-1"]}
          />
          <calcInputs.RangeInput
            id="refinance-rate-input"
            title="New Interest Rate"
            onChange={updateState("refinanceRate", parseFloat)}
            initialValue={refinanceRate}
            min={0}
            max={20}
            step={0.05}
            units="%"
            valueType="percent"
            resetKey={resetKey}
            color={theme["color-1"]}
          />
          <calcInputs.Input id="cash-out-amt-input" title="Cash Out Amount" onChange={updateState("cashOut", util.fromString)} value={cashOut} valueType="currency" />
          <calcInputs.Input id="closing-costs-input" title="Closing Costs" onChange={updateState("closingCosts", util.fromString)} value={closingCosts} valueType="currency" />
        </div>
      </div>
    </div>
  )
}

const Graph = ({props, state, graphData, isDisabled, breakEven}) => {
  const {theme} = props

  return (
    <>
      {!isDisabled && (
        <Line
          ariaLabel="Chart demonstrating how refinanced mortgage compares to current mortgage."
          yAxis="Loan Amount"
          values={loan.getYears(graphData)}
          colors={["#5C5761", graphUtil.getColor(theme, 1)]}
          type="calculator"
          referenceLine={breakEven ? Math.round(breakEven / 12) : null}
          refLineLabel={"Break Even"}
          tooltip={["Year", {name: "Current", type: "currency", color: "#5C5761"}, {name: "Refinanced", type: "currency", color: graphUtil.getColor(theme, 1)}]}
        />
      )}
      {isDisabled && <div className={styles["disable-message"]}>Invalid results. Try adjusting your inputs.</div>}
    </>
  )
}

const Callouts = ({focus, currentPayment, currentTotal, refinancePayment, refinanceTotal, breakEven, costIncrease, isDisabled}) => {
  if (focus === "Term Length") {
    return (
      <TermLengthCallouts
        currentTotalCost={dollars(currentTotal)}
        newTotalCost={dollars(refinanceTotal)}
        totalSavings={dollars(currentTotal - refinanceTotal)}
        breakEven={breakEven}
        isDisabled={isDisabled}
      />
    )
  } else {
    return (
      <PaymentsCallouts
        currentPayment={dollars(currentPayment)}
        newPayment={refinancePayment === Infinity ? "$Infinity" : dollars(refinancePayment)}
        totalCostIncrease={dollars(costIncrease)}
        isDisabled={isDisabled}
      />
    )
  }
}

const dollars = (num) => util.formatDollarsNoCents(util.ifNegative(Number(Math.round(num))))

const canBePaidOff = (b, r, p) =>
  finance.canBePaidOff({
    balance: b,
    rate: r,
    payment: p
  })
const getTerm = (focus, current) => {
  // current will be in months
  const term = Math.round(current / 12)
  if (focus === "Term Length") {
    if (term > 30) {
      return 360
    } else if (term > 15 && term <= 30) {
      return 180
    } else if (term > 10 && term <= 15) {
      return 120
    } else if (term > 5 && term <= 10) {
      return 60
    } else {
      return 12
    }
  } else {
    // if they're trying to reduce payments
    return 360
  }
}

// page two of wizard
const refinanceGraph = ({updateState, props, state}) => {
  const {focus, balance, currentPayment, currentRate, cashOut, closingCosts, refinanceTerm, refinanceRate} = state
  const _currentRate = currentRate / 100
  const refinanceLoanAmount = balance + cashOut + closingCosts
  const currentTerm = finance.calcMonthsToPayoffFixedLoan(balance, _currentRate / 12, currentPayment)
  const isDisabled = currentTerm / 12 > 40 || !canBePaidOff(balance, _currentRate, currentPayment)
  const refTerm = isDisabled ? 360 : refinanceTerm || getTerm(focus, currentTerm)
  const {data, interestCurrent, interestRefinance, refinanceMonthly, breakEven} = refinance.validateData(isDisabled, () =>
    graphDataMortgage(
      balance,
      currentPayment,
      _currentRate,
      currentTerm,
      refTerm, // in months
      refinanceRate / 100,
      cashOut,
      closingCosts
    )
  )
  const currentTotal = balance + interestCurrent
  const newTotal = refinanceLoanAmount + interestRefinance

  const accessibilityTableHeadings = [
    {
      name: "Year",
      key: "year"
    },
    {
      name: "Current Loan",
      key: "current"
    },
    {
      name: "Refinanced Loan",
      key: "refinance"
    }
  ]

  const accessibilityTableData = loan.getYears(data).map((row) => ({
    year: row[0],
    current: util.formatDollarsNoCents(row[1]),
    refinance: util.formatDollarsNoCents(row[2])
  }))

  const accessibilityTableLabel = "Loan Refinance Comparison"

  return (
    <div className={styles["second-page"]}>
      <shared.SummaryCalcContainer>
        <shared.ChartContainer data-testid="refinance-chart">
          <Graph
            props={props}
            state={state}
            updateState={updateState}
            graphData={data}
            isDisabled={isDisabled}
            breakEven={breakEven}
            currentTerm={currentTerm}
            refTerm={Math.round(refTerm / 12)}
          ></Graph>
        </shared.ChartContainer>
        <shared.SummaryContainer className={styles["summary"]}>
          <Callouts
            focus={focus}
            currentPayment={currentPayment}
            currentTotal={currentTotal}
            refinancePayment={refinanceMonthly}
            refinanceTotal={newTotal}
            breakEven={breakEven}
            costIncrease={newTotal - currentTotal}
            isDisabled={isDisabled}
          />
        </shared.SummaryContainer>
        <shared.InputsContainer>
          <Inputs
            props={props}
            state={state}
            updateState={updateState}
            graphData={data}
            isDisabled={isDisabled}
            breakEven={breakEven}
            currentTerm={currentTerm}
            refTerm={Math.round(refTerm / 12)}
          />
        </shared.InputsContainer>
      </shared.SummaryCalcContainer>

      <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
    </div>
  )
}

const MortgageRefinanceCalculator_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey} = getState()
    const {steps, theme} = getProps()

    return (
      <div className={styles["wizard"]}>
        <Wizard
          resetKey={resetKey}
          onReset={onReset}
          setup={{
            steps,
            theme,
            props: {
              className: styles["mortgage-refinance"],
              setState,
              updateState,
              state: getState(),
              props: getProps()
            }
          }}
        />
      </div>
    )
  },
  {
    defaultProps: {
      className: styles["mortgage-refinance"],
      steps: [
        {
          title: "Mortgage Refinancing",
          subtitle: "See how refinancing can help you reduce your monthly payment or pay off your loan sooner!",
          content: currentLoanDetails,
          isStepComplete: ({state}) => {
            const {balance, currentPayment} = state
            return balance > 0 && currentPayment > 0
          },
          buttonText: "Next"
        },
        {
          title: "Mortgage Refinancing",
          content: refinanceGraph,
          isStepComplete: () => {}
        }
      ]
    },
    stateFilter: (state) => {
      const {balance, currentRate, currentPayment, refinanceTerm, refinanceRate, cashOut, closingCosts} = state
      const _currentRate = currentRate / 100
      const currentTerm = finance.calcMonthsToPayoffFixedLoan(balance, _currentRate / 12, currentPayment)
      const isDisabled = currentTerm / 12 > 40 || !canBePaidOff(balance, _currentRate, currentPayment)
      const refTerm = isDisabled ? 360 : refinanceTerm || Math.round(currentTerm * 0.8)

      const {interestCurrent, interestRefinance, refinanceMonthly, breakEven} = refinance.validateData(isDisabled, () =>
        graphDataMortgage(
          balance,
          currentPayment,
          _currentRate,
          currentTerm,
          refTerm, // in months
          refinanceRate / 100,
          cashOut,
          closingCosts
        )
      )

      const refinanceLoanAmount = balance + cashOut + closingCosts
      const currentTotal = balance + interestCurrent
      const newTotal = refinanceLoanAmount + interestRefinance
      const newPayment = refinanceMonthly === Infinity ? "$Infinity" : refinanceMonthly

      return Object.assign(state, {
        interestCurrent,
        interestRefinance,
        breakEven,
        newTotal,
        currentTotal,
        newPayment
      })
    },
    defaultState: {
      balance: "",
      currentPayment: "",
      currentRate: 3.65,
      focus: "Term Length",
      refinanceTerm: null,
      refinanceRate: 3.65,
      cashOut: 0,
      closingCosts: 4000
    }
  }
)

export default (props) => <MortgageRefinanceCalculator_ {...props} />
